import 'fslightbox';

import { Background } from './background.js';

class Application extends HTMLElement {
	#background = new Background();

	/** @type {URL | undefined} */
	#currentUrl;

	#pauseBackgroundBtn;
	#playBackgroundBtn;
	#hideContentBtn;
	#showContentBtn;

	constructor() {
		super();

		const appControlsTpl = /** @type {HTMLTemplateElement} */(document.getElementById('app-controls-template'));
		const appControls = this.appendChild(/** @type {DocumentFragment} */appControlsTpl.content.cloneNode(true));

		this.#pauseBackgroundBtn = /** @type {HTMLButtonElement} */(this.querySelector('#pause-background'));
		this.#playBackgroundBtn = /** @type {HTMLButtonElement} */(this.querySelector('#play-background'));
		this.#hideContentBtn = /** @type {HTMLButtonElement} */(this.querySelector('#hide-content'));
		this.#showContentBtn = /** @type {HTMLButtonElement} */(this.querySelector('#show-content'));

		this.#pauseBackgroundBtn.onclick = (e) => {
			e.preventDefault();
			this.#background.stopAnimation();
			localStorage.setItem('backgroundPaused', 'true');
			this.#playBackgroundBtn.hidden = false;
			this.#pauseBackgroundBtn.hidden = true;
		};

		this.#playBackgroundBtn.onclick = (e) => {
			e.preventDefault();
			this.#background.startAnimation();
			localStorage.setItem('backgroundPaused', 'false');
			this.#playBackgroundBtn.hidden = true;
			this.#pauseBackgroundBtn.hidden = false;
		};

		this.#hideContentBtn.onclick = (e) => {
			e.preventDefault();
			this.#background.classList.add('foregrounded');
			this.#hideContentBtn.hidden = true;
			this.#showContentBtn.hidden = false;
		};

		this.#showContentBtn.onclick = (e) => {
			e.preventDefault();
			this.#background.classList.remove('foregrounded');
			this.#hideContentBtn.hidden = false;
			this.#showContentBtn.hidden = true;
		};

		this.applyState();
		this.append(this.#background, appControls);
	}

	storeState() {
		localStorage.setItem(
			'backgroundState',
			JSON.stringify(this.#background.getState()),
		);
	}

	applyState() {
		const isBackgroundPaused = localStorage.getItem('backgroundPaused') === 'true';
		const backgroundState = localStorage.getItem('backgroundState');

		this.#pauseBackgroundBtn.hidden = isBackgroundPaused;
		this.#playBackgroundBtn.hidden = !isBackgroundPaused;

		if (!isBackgroundPaused) this.#background.startAnimation();
		if (backgroundState) this.#background.applyState(JSON.parse(backgroundState));
	}

	resize() {
		this.#background.resize();
	}

	showContent() {
		this.#showContentBtn.click();
	}

	hideContent() {
		this.#hideContentBtn.click();
	}

	/**
	 * @param {URL} url
	 * @param {NavigateEvent & { target: EventTarget & { activation?: NavigationActivation } | null }} [navigateEvent]
	 */
	async loadSection(url, navigateEvent) {
		if (navigateEvent != null) {
			const request = await globalThis.fetch(url);
			const page = new DOMParser().parseFromString(await request.text(), 'text/html');

			for (const element of page.querySelectorAll('header,main,footer')) {
				const tagName = element.tagName.toLowerCase();
				const existingElement = /** @type {Element} */((document.querySelector(tagName)));
				document.body.replaceChild(document.adoptNode(element), existingElement);
			}
		}

		/** @type {any} */(globalThis).refreshFsLightbox();

		const section = url.pathname.split('/').filter(Boolean).at(0);
		const currentUrl = this.#currentUrl?.pathname;
		this.#currentUrl = url;

		if (section == null || currentUrl?.includes(section)) {
			return;
		}

		if (navigateEvent != null) {
			const { default: routes } = await import('./routes.json', { with: { type: 'json' } });
			if (routes.includes(section) === false) {
				return;
			}
		}

		const { default: listeners } = await import(`./routes/${section}.js`);
		const listenerEntries = Object.entries(listeners).map(([x, y]) => [x.toLowerCase(), y]);

		for (const [event, listener] of listenerEntries) {
			if (event.startsWith('navigate')) {
				listener(navigateEvent);
				globalThis.navigation.addEventListener(event, listener);
			} else {
				globalThis.addEventListener(event, listener);
			}
		}

		const _teardownListener = (/** @type {NavigateEvent} */e) => {
			if (e.destination.url.includes(section) === false) {
				for (const [event, listener] of listenerEntries) {
					if (event.startsWith('navigate')) {
						globalThis.navigation.removeEventListener(event, listener);
					} else {
						globalThis.removeEventListener(event, listener);
					}
				}
				globalThis.navigation.removeEventListener('navigate', _teardownListener);
			}
		};
		globalThis.navigation.addEventListener('navigate', _teardownListener);
	}
}

globalThis.customElements.define('app-background', Background);
globalThis.customElements.define('app-container', Application);

const application = new Application();
document.body.append(application);
await application.loadSection(new URL(location.href));

globalThis.addEventListener('resize', () => application.resize());
globalThis.addEventListener('visibilitychange', () => {
	if (document.visibilityState === 'hidden') application.storeState();
	if (document.visibilityState === 'visible') application.applyState();
});

globalThis.navigation?.addEventListener('navigate', (e) => {
	if (!e.canIntercept || e.hashChange || e.downloadRequest != null || e.formData != null)
		return;

	const { destination: { url } } = e;

	// non-html asset request
	if (/[.]\w+$/g.test(url) && url.endsWith('.html') === false)
		return;

	e.intercept({
		async handler() {
			await application.loadSection(new URL(url), e);
			application.showContent();
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	});
});
